import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { InviteStatePanel } from "@/components/auth/InviteStatePanel";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { reload } = useAuth();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(token ? { status: "checking" } : { status: "invalid" });

  const check = useCallback(() => {
    setState({ status: "checking" });
    api.get("/auth/invitation-status", { params: { token } }).then((r) => setState(r.data)).catch(() => setState({ status: "error" }));
  }, [token]);
  useEffect(() => { if (token) check(); }, [token, check]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/accept-invite", { token, name: form.name, password: form.password });
      await reload();
      toast.success(`Welcome to ${state.workspace_name}`);
      nav("/", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail || "";
      if (/already been used/i.test(d)) return setState({ status: "used" });
      if (/expired/i.test(d)) return setState({ status: "expired" });
      toast.error(apiError(err, "Invitation could not be accepted"));
    } finally { setBusy(false); }
  };

  if (state.status === "checking") return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400 text-sm" data-testid="invite-checking">Verifying your invitation…</div>;
  if (state.status !== "valid") return <InviteStatePanel status={state.status} onRetry={check} />;

  const input = "w-full bg-slate-950/60 border border-slate-800 focus:border-amber-500 rounded px-3 py-2 text-sm text-slate-100";
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6" data-testid="accept-invite-page">
      <form onSubmit={submit} className="sc-card p-8 w-full max-w-md space-y-4">
        <h1 className="font-heading text-2xl font-extrabold text-slate-100">{state.existing_user ? "Join the workspace" : "Set up your account"}</h1>
        <p className="text-sm text-slate-400" data-testid="invite-summary">
          You're invited to <b className="text-slate-200">{state.workspace_name}</b> as <b className="text-amber-400 uppercase">{state.role}</b> ({state.email_hint}). {state.existing_user ? "Confirm your password to join." : "Choose your own password — invitations are single-use and expire after 72 hours."}
        </p>
        {!state.existing_user && <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="invite-name" />}
        <input type="password" placeholder="Password (min 8 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} required minLength={8} data-testid="invite-password" />
        <input type="password" placeholder="Confirm password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={input} required data-testid="invite-confirm" />
        <button disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2 rounded disabled:opacity-50" data-testid="invite-submit">{busy ? "Joining…" : state.existing_user ? "Join workspace" : "Create account & continue"}</button>
        <div className="text-xs text-slate-500">Already have an account? <Link to="/login" className="text-amber-400 underline">Sign in</Link></div>
      </form>
    </div>
  );
}
